// src/utils/barcodeUtils.ts - Version finale pour votre projet

export enum BarcodeFormat {
    EAN13 = 'EAN-13',
    UPC_A = 'UPC-A',
    CODE128 = 'Code-128',
    INTERNAL = 'INTERNAL',
    UNKNOWN = 'UNKNOWN'
  }
  
  export interface BarcodeValidationResult {
    isValid: boolean;
    format: BarcodeFormat;
    cleanCode: string;
    errors: string[];
  }
  
  // Nettoyage du code-barres
  export const cleanBarcode = (barcode: string): string => {
    return barcode.replace(/[^0-9A-Za-z-]/g, '').toUpperCase();
  };
  
  // Validation EAN-13
  export const validateEAN13 = (code: string): boolean => {
    if (!/^\d{13}$/.test(code)) return false;
    const digits = code.split('').map(Number);
    const checkDigit = digits.pop()!;
    let sum = 0;
    for (let i = 0; i < 12; i++) {
      sum += digits[i] * (i % 2 === 0 ? 1 : 3);
    }
    const calculatedCheckDigit = (10 - (sum % 10)) % 10;
    return calculatedCheckDigit === checkDigit;
  };
  
  // Validation UPC-A
  export const validateUPC_A = (code: string): boolean => {
    if (!/^\d{12}$/.test(code)) return false;
    const digits = code.split('').map(Number);
    const checkDigit = digits.pop()!;
    let sum = 0;
    for (let i = 0; i < 11; i++) {
      sum += digits[i] * (i % 2 === 0 ? 3 : 1);
    }
    const calculatedCheckDigit = (10 - (sum % 10)) % 10;
    return calculatedCheckDigit === checkDigit;
  };
  
  // Validation Code-128
  export const validateCode128 = (code: string): boolean => {
    // Code-128 doit contenir au moins 4 caractères et respecter certaines contraintes
    if (code.length < 4 || code.length > 48) return false;
    
    // Code-128 typique : mélange de lettres et chiffres, pas seulement des chiffres
    // (les codes purement numériques sont plutôt EAN-13/UPC-A)
    if (/^\d+$/.test(code)) return false;
    
    // Doit contenir des caractères ASCII imprimables valides pour Code-128
    return /^[!-~]{4,48}$/.test(code);
  };
  
  // Validation codes internes
  export const validateInternalCode = (code: string): boolean => {
    return /^[A-Z]{3}-\d{6}$/.test(code);
  };
  
  // Détection automatique du format
  export const detectBarcodeFormat = (code: string): BarcodeFormat => {
    const cleanCode = cleanBarcode(code);
    
    if (validateEAN13(cleanCode)) return BarcodeFormat.EAN13;
    if (validateUPC_A(cleanCode)) return BarcodeFormat.UPC_A;
    if (validateInternalCode(cleanCode)) return BarcodeFormat.INTERNAL;
    if (validateCode128(cleanCode)) return BarcodeFormat.CODE128;
    
    return BarcodeFormat.UNKNOWN;
  };
  
  // Validation complète
  export const validateBarcode = (barcode: string): BarcodeValidationResult => {
    const errors: string[] = [];
    
    if (!barcode || barcode.trim().length === 0) {
      return {
        isValid: false,
        format: BarcodeFormat.UNKNOWN,
        cleanCode: '',
        errors: ['Le code-barres ne peut pas être vide']
      };
    }
  
    const cleanCode = cleanBarcode(barcode);
    
    if (cleanCode.length < 3) {
      errors.push('Le code-barres doit contenir au moins 3 caractères');
    }
  
    if (cleanCode.length > 48) {
      errors.push('Le code-barres ne peut pas dépasser 48 caractères');
    }
  
    const format = detectBarcodeFormat(cleanCode);
    
    if (format === BarcodeFormat.UNKNOWN) {
      errors.push('Format de code-barres non reconnu');
    }
  
    return {
      isValid: errors.length === 0,
      format,
      cleanCode,
      errors
    };
  };
  
  // Génération de codes internes
  export const generateInternalCode = (
    businessType: string, 
    sequence: number,
    categoryPrefix?: string
  ): string => {
    const businessPrefixes: Record<string, string> = {
      'beauty_salon': 'BEA',
      'bakery': 'BAK',
      'electromenager': 'ELE',
      'restaurant': 'RES',
      'retail_store': 'RET',
      'garage': 'GAR',
      'prosthesis_clinic': 'PRO',
      'custom': 'CUS'
    };
  
    let prefix = businessPrefixes[businessType] || 'GEN';
    
    if (categoryPrefix) {
      prefix = categoryPrefix.substring(0, 3).toUpperCase().padEnd(3, 'X');
    }
  
    const sequenceStr = sequence.toString().padStart(6, '0');
    return `${prefix}-${sequenceStr}`;
  };
  
  // Extraction de séquence
  export const extractSequenceFromInternalCode = (code: string): number => {
    const match = code.match(/^[A-Z]{3}-(\d{6})$/);
    return match ? parseInt(match[1], 10) : 0;
  };
  
  // Formatage pour affichage
  export const formatBarcodeForDisplay = (barcode: string, format: BarcodeFormat): string => {
    switch (format) {
      case BarcodeFormat.EAN13:
        return barcode.replace(/(\d{1})(\d{6})(\d{6})/, '$1 $2 $3');
      case BarcodeFormat.UPC_A:
        return barcode.replace(/(\d{6})(\d{6})/, '$1 $2');
      case BarcodeFormat.INTERNAL:
        return barcode;
      default:
        return barcode;
    }
  };